import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { prefereMovimentoReduzido, observarMovimentoReduzido } from '../../utils/movimentoReduzido';

/**
 * O fundo animado do cabeçalho dos indicadores.
 *
 * Um campo de pontos girando devagar, na cor que vier por `cor`. É enfeite:
 * nada da tela depende dele, e ele fica atrás de tudo, sem receber clique.
 *
 * ## Três decisões que não são estéticas
 *
 * **1. O `three` entra por import dinâmico.** A biblioteca tem algumas
 * centenas de kB; esta é a tela INICIAL do operador, a que abre dezenas de
 * vezes por dia. Com o import dentro do efeito, ela vira um pedaço separado
 * que o navegador busca depois da tela já estar de pé -- e quem prefere
 * movimento reduzido nunca o baixa.
 *
 * **2. Pausa quando a aba sai da frente.** Um painel de indicadores fica
 * aberto o dia inteiro atrás de outras janelas. Sem a pausa, isto seria um
 * laço de animação consumindo processador e bateria para desenhar algo que
 * ninguém está vendo.
 *
 * **3. Devolve o contexto de WebGL ao sair.** O navegador limita quantos
 * contextos existem ao mesmo tempo; navegar para fora e voltar várias vezes
 * sem liberar acaba em tela preta e um aviso no console, longe daqui.
 *
 * Em navegador sem WebGL, ou se o import falhar, o componente simplesmente
 * não desenha nada -- e o cabeçalho fica com o gradiente de sempre.
 */
const FundoAnimado = ({ cor = '#63ACE5', opacidade = 0.5 }) => {
    const container = useRef(null);
    const corRef = useRef(cor);

    corRef.current = cor;

    useEffect(() => {
        if (prefereMovimentoReduzido()) {
            // Nem carrega a biblioteca.
            return undefined;
        }

        let vivo = true;
        let limpar = () => {};

        import('three')
            .then((THREE) => {
                if (!vivo || !container.current) return;

                const alvo = container.current;
                const largura = alvo.clientWidth || 1;
                const altura = alvo.clientHeight || 1;

                let renderer;

                try {
                    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
                } catch {
                    // Sem WebGL: o cabeçalho fica com o gradiente e pronto.
                    return;
                }

                renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
                renderer.setSize(largura, altura);
                renderer.domElement.setAttribute('aria-hidden', 'true');
                renderer.domElement.style.display = 'block';
                alvo.appendChild(renderer.domElement);

                const cena = new THREE.Scene();
                const camera = new THREE.PerspectiveCamera(60, largura / altura, 0.1, 100);
                camera.position.z = 6;

                const QUANTOS = 420;
                const posicoes = new Float32Array(QUANTOS * 3);

                for (let i = 0; i < QUANTOS; i += 1) {
                    posicoes[i * 3] = (Math.random() - 0.5) * 18;
                    posicoes[i * 3 + 1] = (Math.random() - 0.5) * 8;
                    posicoes[i * 3 + 2] = (Math.random() - 0.5) * 10;
                }

                const geometria = new THREE.BufferGeometry();
                geometria.setAttribute('position', new THREE.BufferAttribute(posicoes, 3));

                const material = new THREE.PointsMaterial({
                    color: new THREE.Color(corRef.current),
                    size: 0.055,
                    transparent: true,
                    opacity: opacidade,
                    depthWrite: false,
                });

                const pontos = new THREE.Points(geometria, material);
                cena.add(pontos);

                let quadro = null;
                let rodando = true;

                const desenhar = () => {
                    if (!vivo) return;

                    // Devagar de propósito: é fundo de tela de trabalho, não
                    // protetor de tela.
                    pontos.rotation.y += 0.0012;
                    pontos.rotation.x += 0.0004;
                    renderer.render(cena, camera);
                    quadro = requestAnimationFrame(desenhar);
                };

                const parar = () => {
                    rodando = false;
                    if (quadro) cancelAnimationFrame(quadro);
                    quadro = null;
                };

                const retomar = () => {
                    if (rodando || !vivo) return;
                    rodando = true;
                    desenhar();
                };

                const aoTrocarVisibilidade = () => {
                    if (document.hidden) parar();
                    else retomar();
                };

                const aoRedimensionar = () => {
                    if (!vivo || !container.current) return;

                    const l = container.current.clientWidth || 1;
                    const a = container.current.clientHeight || 1;

                    camera.aspect = l / a;
                    camera.updateProjectionMatrix();
                    renderer.setSize(l, a);
                };

                desenhar();
                document.addEventListener('visibilitychange', aoTrocarVisibilidade);
                window.addEventListener('resize', aoRedimensionar);

                limpar = () => {
                    parar();
                    document.removeEventListener('visibilitychange', aoTrocarVisibilidade);
                    window.removeEventListener('resize', aoRedimensionar);
                    geometria.dispose();
                    material.dispose();
                    renderer.dispose();

                    // Devolve o contexto de WebGL: o navegador limita quantos
                    // existem ao mesmo tempo.
                    const perda = renderer.getContext()
                        && renderer.getContext().getExtension('WEBGL_lose_context');
                    if (perda) perda.loseContext();

                    if (renderer.domElement.parentNode) {
                        renderer.domElement.parentNode.removeChild(renderer.domElement);
                    }
                };
            })
            .catch(() => {
                // Pedaço que não baixou (rede ruim, bloqueio): sem fundo.
            });

        const pararDeObservar = observarMovimentoReduzido((reduzido) => {
            // Ligou a preferência com a tela aberta: para na hora, sem
            // precisar recarregar.
            if (reduzido) limpar();
        });

        return () => {
            vivo = false;
            pararDeObservar();
            limpar();
        };
    }, [opacidade]);

    return (
        <Box
            ref={container}
            aria-hidden="true"
            data-testid="fundo-animado"
            sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                overflow: 'hidden',
                // Fica atrás do conteúdo do cabeçalho, nunca na frente.
                zIndex: 0,
            }}
        />
    );
};

export default FundoAnimado;
